import type { WorkflowFacet } from "../api/workflow";

/** The facet whose groups drive the Status filter. */
export const STATUS_FILTER_FACET = "all_suggestions";

export interface StatusGroup {
  name: string;
  label: string;
  states: string[];
}

/**
 * The Status filter's options: the facet's statuses in
 * `status_display_order` (unlisted ones after, in yaml order), minus the
 * catch-all `all` and any status with an empty state set.
 *
 * Returns null when the facet is missing or any status other than `all`
 * lacks `states` — an older backend — so the caller falls back to listing
 * raw workflow states.
 */
export function statusGroupsFromFacet(
  facet: WorkflowFacet | undefined,
): StatusGroup[] | null {
  if (!facet) return null;
  const statuses = facet.statuses.filter((s) => s.name !== "all");
  if (statuses.length === 0 || statuses.some((s) => !s.states)) return null;
  const order = facet.status_display_order ?? [];
  const rank = (name: string) => {
    const i = order.indexOf(name);
    return i === -1 ? order.length : i;
  };
  return statuses
    .map((s, i) => ({ s, i }))
    .sort((a, b) => rank(a.s.name) - rank(b.s.name) || a.i - b.i)
    .map(({ s }) => ({ name: s.name, label: s.label, states: s.states ?? [] }))
    .filter((g) => g.states.length > 0);
}

/**
 * Read a raw-state filter value as groups. A group is selected only when
 * every one of its states is in the value; any state not covered by a
 * selected group is a leftover — typically a single state from an older
 * link (`?status=implemented`) — and keeps filtering by itself.
 */
export function selectedStatusGroups(
  groups: StatusGroup[],
  value: string[],
): { selected: StatusGroup[]; leftovers: string[] } {
  const inValue = new Set(value);
  const selected = groups.filter((g) => g.states.every((s) => inValue.has(s)));
  const covered = new Set(selected.flatMap((g) => g.states));
  const leftovers = value.filter((s) => !covered.has(s));
  return { selected, leftovers };
}

/** The raw-state filter value for a set of groups plus leftovers. */
export function statusesForGroups(
  groups: StatusGroup[],
  leftovers: string[] = [],
): string[] {
  return Array.from(new Set([...groups.flatMap((g) => g.states), ...leftovers]));
}
